import {AdyenPaymentsService} from '../../../../../../services/adyen/payments'
import {AdyenPaymentsDetailsService} from '@adyenPWA/lib/services/payments-details'
import {executeCallbacks, executeErrorCallbacks} from '@adyenPWA/lib/utils/executeCallbacks'
import {getCurrencyValueForApi} from '@adyenPWA/lib/utils/parsers.mjs'
import {CART_HREF} from '../../../../constants.js'
import {PaymentCancelService} from '@adyenPWA/lib/services/payment-cancel'
// Utils
import {getConvertedCurrencyCode, getConvertedPrice} from '../../../../utils/price-utils'

export const baseConfig = (props) => {
    const {
        beforeSubmit = [],
        afterSubmit = [],
        beforeAdditionalDetails = [],
        afterAdditionalDetails = [],
        onError = []
    } = props
    // Create error handler with props in closure
    const errorHandler = (error, component) => onErrorHandler(error, component, props)

    const amount = getAmount(props)
    return {
        amount,
        onSubmit: executeCallbacks(
            [...beforeSubmit, onSubmit, ...afterSubmit, onPaymentsSuccess],
            props
        ),
        onAdditionalDetails: executeCallbacks(
            [
                ...beforeAdditionalDetails,
                onAdditionalDetails,
                ...afterAdditionalDetails,
                onPaymentsDetailsSuccess
            ],
            props
        ),
        setStatusAutomatically: false,
        onError: executeErrorCallbacks([...onError, errorHandler], props),
        onPaymentFailed: executeErrorCallbacks([...onError, errorHandler], props)
    }
}

export const onSubmit = async (state, component, actions, props) => {
    const activeComponent = component.dropinRef || component

    if (!state.isValid) {
        throw new Error('invalid state')
    }

    if (
        state.termsAndConditionsInvalid ||
        state.billingAddressInvalid ||
        state.insufficientInventory ||
        state.pendingShippingAdjustment
    ) {
        if (state.data.paymentMethod.type === 'paypal') {
            activeComponent?.setStatus?.('error')
            if (state.insufficientInventory) {
                activeComponent?.setStatus?.('ready')
                props.navigate(CART_HREF)
            } else {
                setTimeout(() => {
                    activeComponent?.setStatus?.('ready')
                }, 3000)
            }
        } else {
            activeComponent?.setStatus?.('ready')
            if (state.insufficientInventory) {
                props.navigate(CART_HREF)
            }
        }
        return
    }

    try {
        activeComponent?.setStatus?.('loading')
        const token = typeof props?.token === 'function' ? props.token() : props?.token
        const basket = props.getBasket ? props.getBasket() : props.basket
        const adyenPaymentService = new AdyenPaymentsService(
            token,
            props?.customerId,
            basket?.basketId,
            props?.site
        )

        const paymentsResponse = await adyenPaymentService.submitPayment(
            {
                ...state.data,
                origin: state.data.origin ? state.data.origin : window.location.origin,
                returnUrl:
                    props?.returnUrl ||
                    `${window.location.origin}${window.location.pathname}/redirect`,
                utmParameters: state.utmParameters,
                endDigits: props?.cardDetails?.endDigits,
                issuerBin: props?.cardDetails?.issuerBin,
                riskifiedBeacon: state.riskifiedBeacon
            },
            props.locale
        )
        /*
            When a payment method requires redirect to external service if the user
            get back to site by using the back button, we need to set a flag in localStorage
        */
        if (localStorage && paymentsResponse?.action?.type === 'redirect') {
            localStorage.setItem('isRedirectedToExternalPayment', 'true')
        }
        return {paymentsResponse: paymentsResponse}
    } catch (err) {
        actions.reject(err)

        if (err?.newBasketId) {
            props.navigate(
                `/checkout/error?paymentFailed=true&newBasketId=${encodeURIComponent(
                    err.newBasketId
                )}`
            )
        } else {
            props.navigate('/checkout/error')
        }

        throw err
    }
}

export const onAdditionalDetails = async (state, component, actions, props) => {
    try {
        const basket = props.getBasket ? props.getBasket() : props.basket
        const adyenPaymentsDetailsService = new AdyenPaymentsDetailsService(
            props?.token,
            props?.customerId,
            basket?.basketId,
            props?.site
        )
        const paymentsDetailsResponse = await adyenPaymentsDetailsService.submitPaymentsDetails(
            state.data,
            props?.customerId
        )
        return {paymentsDetailsResponse: paymentsDetailsResponse}
    } catch (err) {
        actions.reject(err)
        props?.navigate('/checkout/error?paymentDetailsFailed=true')
        throw err
    }
}

export const onErrorHandler = async (error, component, props) => {
    try {
        const urlParams = new URLSearchParams(window.location.search)
        const isRedirectReturn = Boolean(urlParams.get('redirectResult'))

        if (isRedirectReturn) {
            props.navigate('/checkout/error?paymentDetailsFailed=true')
            return {cancelled: true}
        }

        let newBasketId = error?.newBasketId
        const basket = props.getBasket ? props.getBasket() : props.basket

        if (!newBasketId) {
            const paymentCancelService = new PaymentCancelService(
                props.token,
                props.customerId,
                basket?.basketId,
                props.site
            )

            const cancelResponse = await paymentCancelService.paymentCancel(props.orderNo)

            if (cancelResponse?.newBasketId) {
                newBasketId = cancelResponse.newBasketId
            }
        }

        if (props.adyenOrder) {
            props.setAdyenOrder(null)
        }

        if (newBasketId) {
            props.navigate(
                `/checkout/error?paymentFailed=true&newBasketId=${encodeURIComponent(newBasketId)}`
            )
        }

        return {cancelled: true}
    } catch (err) {
        console.error('Error during payment cancellation:', err)
        return {cancelled: false, error: err.message}
    }
}

export const getAmount = ({basket, adyenOrder}) => {
    if (!basket) return null
    if (adyenOrder) {
        return adyenOrder.remainingAmount
    }

    // export conversion rate fields from basket
    const conversionsRate = {
        conversionRate: basket?.c_conversionRate,
        lcpMultiplier: basket?.c_lcpMultiplier,
        paymentCurrencyCode: basket?.c_paymentCurrencyCode,
        usePriceConverter: basket?.c_usePriceConverter || false,
        internationalShipping: basket?.c_internationalShipping || false
    }

    const currencyCode = conversionsRate?.usePriceConverter
        ? basket?.c_paymentCurrencyCode
        : basket.currency

    // On a redirect/action return (e.g. Klarna) the current basket can be a fresh empty
    // basket with no currency. The amount is only needed to render the payment UI, not to
    // submit the redirect details, so return null instead of throwing "invalid currency!".
    if (!currencyCode) return null

    const finalTotal =
        basket?.c_internationalShipping && basket?.c_internationalShippingOrderQuote
            ? basket?.c_basketTotalWithGiftCertificatesApplied ||
              basket?.c_internationalShippingOrderQuote
            : getConvertedPrice(
                  basket?.c_basketTotalWithGiftCertificatesApplied,
                  conversionsRate,
                  false
              )

    return {
        value: getCurrencyValueForApi(finalTotal, currencyCode),
        currency: getConvertedCurrencyCode(conversionsRate, basket.currency)
    }
}

const handleAction = (navigate, setAdyenAction, component, response) => {
    const {action, merchantReference} = response
    const actionURI = btoa(JSON.stringify(action))
    const url = `/checkout/confirmation/${merchantReference}?adyenAction=${actionURI}`
    switch (action.type) {
        case 'voucher':
            navigate(url)
            break
        case 'threeDS2':
            setAdyenAction(actionURI)
            break
        default:
            // Push orderNo to history so it's readable on browser back navigation.
            // The useHandleBackNavigation hook reads this to pass to the cancel endpoint.
            if (merchantReference) {
                const params = new URLSearchParams(window.location.search)
                params.set('orderNo', merchantReference)
                window.history.pushState(
                    null,
                    '',
                    `${window.location.pathname}?${params.toString()}`
                )
            }
            component.handleAction(action)
            break
    }
}

export const onPaymentsSuccess = async (state, component, actions, props, responses) => {
    if (!responses?.paymentsResponse) return

    if (responses?.paymentsResponse?.merchantReference) {
        if (props?.orderNo !== responses?.paymentsResponse?.merchantReference) {
            props?.setOrderNo(responses?.paymentsResponse?.merchantReference)
        }
    }
    if (responses?.paymentsResponse?.order?.orderData) {
        // Only update the order if it's different from the one in the state to prevent re-renders.
        if (props.adyenOrder?.orderData !== responses?.paymentsResponse?.order?.orderData) {
            props?.setAdyenOrder(responses?.paymentsResponse?.order)
        }
    }
    if (responses?.paymentsResponse?.isSuccessful && responses?.paymentsResponse?.isFinal) {
        if (responses?.paymentsResponse?.order) {
            if (responses?.paymentsResponse?.order?.remainingAmount?.value <= 0) {
                props?.navigate(
                    `/checkout/confirmation/${responses?.paymentsResponse?.merchantReference}`
                )
            }
        } else {
            props?.navigate(
                `/checkout/confirmation/${responses?.paymentsResponse?.merchantReference}`
            )
        }
    } else if (responses?.paymentsResponse?.riskifiedDenied) {
        props?.navigate('/checkout/error')
    } else if (responses?.paymentsResponse?.action) {
        handleAction(props?.navigate, props?.setAdyenAction, component, responses?.paymentsResponse)
    }
    actions.resolve(responses?.paymentsResponse)
}

export const onPaymentsDetailsSuccess = async (state, component, actions, props, responses) => {
    if (responses?.paymentsDetailsResponse?.isSuccessful) {
        props?.navigate(
            `/checkout/confirmation/${responses?.paymentsDetailsResponse?.merchantReference}`
        )
    } else if (responses?.paymentsDetailsResponse?.action) {
        handleAction(
            props?.navigate,
            props?.setAdyenAction,
            component,
            responses?.paymentsDetailsResponse
        )
    }
    actions.resolve(responses?.paymentsDetailsResponse)
}
